// Table mapping module: functions for mapping/copying data between source and target tables
// Handles: Source → Target (document/study) and Target → Target (document → study)
import { logWarning, logError, logInfo } from "../../../services/logging/log-handler.js";

// Re-export validation functions
export {
  validateSourceAgainstTarget,
  getValidationSummary,
  type ValidationResult
} from './validation.js';

// Re-export basic sync functions
export {
  matchOnlySync,
  matchAllSync,
  getSyncSummary
} from './data-sync.js';

/**
 * Build source column key mapping from working data
 * Maps column labels to their keys (used for matching columns between source and target)
 */
export function buildSourceKeyMapping(
  workingData: any,
  tableIndex: number,
  targetId: string,
  sourceHeaders: string[]
): Map<string, string> {
  const mapping = new Map<string, string>();
  
  if (!workingData || tableIndex < 0) return mapping;
  
  // Get current work version
  const getCurrentVersion = (data: any, idx: number, tid: string): any => {
    if (!data.tables || !data.tables[idx]) return null;
    const table: any = data.tables[idx];
    if (Array.isArray(table.work)) {
      return table.work.find((target: any) => target.target === tid) || null;
    }
    return null;
  };
  
  const currentData = getCurrentVersion(workingData, tableIndex, targetId);
  if (!currentData || !currentData.columns) return mapping;
  
  // Map source column labels to their keys
  sourceHeaders.forEach((header: string) => {
    const column = currentData.columns.find((col: any) => col.label === header);
    if (column && column.key) {
      mapping.set(header, column.key);
    }
  });
  
  return mapping;
}

/**
 * Build target column key mapping from project structure
 * Maps column labels to their keys (from project structure)
 * Supports both topic tables and references
 */
export function buildTargetKeyMapping(
  projectStructure: any,
  topicId: string,
  tableId: string,
  targetHeaders: string[],
  referenceId?: string
): Map<string, string> {
  const mapping = new Map<string, string>();
  
  if (!projectStructure || !targetHeaders || targetHeaders.length === 0) return mapping;
  
  // Reference mode: use reference structure
  if (referenceId) {
    const referenceStructure = projectStructure.references?.find((r: any) => r.id === referenceId);
    if (!referenceStructure) return mapping;
    
    // Get columns from structure - can be directly in referenceStructure or in referenceStructure.structure
    const columns = referenceStructure.columns || referenceStructure.structure?.columns || [];
    
    // Map target column labels to their keys (column IDs)
    targetHeaders.forEach((header: string) => {
      const columnStructure = columns.find((col: any) => col.label === header || col.id === header);
      if (columnStructure) {
        mapping.set(header, columnStructure.id);
      }
    });
    
    return mapping;
  }
  
  // Topic mode: use topic/table structure
  if (!topicId || !tableId) return mapping;
  
  const topicStructure = projectStructure.topics?.find((t: any) => t.id === topicId);
  if (!topicStructure) return mapping;
  
  const tableStructure = topicStructure.tables?.find((t: any) => t.id === tableId);
  if (!tableStructure) return mapping;
  
  // Map target column labels to their keys
  targetHeaders.forEach((header: string) => {
    const columnStructure = tableStructure.columns?.find((col: any) => col.label === header);
    if (columnStructure) {
      if (columnStructure.refTable === 'grouper') {
        // For grouper references, use the grouper level ID
        const grouperStructure = topicStructure.grouper;
        if (grouperStructure && grouperStructure.levels && grouperStructure.levels.length > 0) {
          mapping.set(header, grouperStructure.levels[0].id);
        }
      } else {
        // For regular columns, use the column ID
        mapping.set(header, columnStructure.id);
      }
    }
  });
  
  return mapping;
}

/**
 * Find grouper column indices by column names
 */
function findGrouperIndicesByName(headers: any[], grouperColumns: string[]): number[] {
  const indices: number[] = [];
  
  grouperColumns.forEach(grouperLabel => {
    const normalizedLabel = grouperLabel.toLowerCase().trim();
    const index = headers.findIndex((h: any) => 
      h?.toString().toLowerCase().trim() === normalizedLabel
    );
    if (index !== -1) {
      indices.push(index);
    }
  });
  
  return indices;
}

/**
 * Build a row key from grouper column values
 */
function buildRowKeyFromIndices(row: any[], grouperIndices: number[]): string {
  return grouperIndices
    .map(index => row[index]?.toString().toLowerCase().trim() || '')
    .join('|');
}

/**
 * Filter 2D array to only include selected rows (plus header)
 */
export function filterRowsBySelection(data2D: any[][], selectedRows: Set<number>): any[][] {
  if (!data2D || data2D.length === 0) return [];
  
  const result = [data2D[0]]; // Always include header
  
  selectedRows.forEach(rowIndex => {
    const actualRowIndex = rowIndex + 1; // Account for header row
    if (actualRowIndex < data2D.length) {
      result.push(data2D[actualRowIndex]);
    }
  });
  
  return result;
}

/**
 * Match Only Sync with Key-based column mapping
 * Updates existing target rows where grouper values match
 * Uses column keys (not just labels) for accurate mapping
 */
export function matchOnlySyncWithKeys(
  sourceData: any[][],
  targetData: any[][],
  grouperColumns: string[],
  sourceKeyMapping: Map<string, string>,
  targetKeyMapping: Map<string, string>
): any[][] {
  if (!sourceData || sourceData.length === 0 || !targetData || targetData.length === 0) {
    return targetData;
  }
  
  const sourceHeaders = sourceData[0];
  const targetHeaders = targetData[0];
  const sourceRows = sourceData.slice(1);
  const targetRows = targetData.slice(1);
  
  // Find grouper column indices
  const sourceGrouperIndices = findGrouperIndicesByName(sourceHeaders, grouperColumns);
  const targetGrouperIndices = findGrouperIndicesByName(targetHeaders, grouperColumns);
  
  if (sourceGrouperIndices.length === 0 || targetGrouperIndices.length === 0) {
    logWarning('No grouper columns found for matching', "table-mapping", undefined);
    return targetData;
  }
  
  // Build source row map by grouper key
  const sourceRowMap = new Map<string, any[]>();
  sourceRows.forEach((sourceRow: any[]) => {
    const rowKey = buildRowKeyFromIndices(sourceRow, sourceGrouperIndices);
    sourceRowMap.set(rowKey, sourceRow);
  });
  
  // Build column mapping by keys
  const columnMapping = new Map<number, number>();
  sourceHeaders.forEach((sourceHeader: string, sourceIndex: number) => {
    const sourceKey = sourceKeyMapping.get(sourceHeader);
    if (sourceKey) {
      // Find target column with same key
      const targetIndex = targetHeaders.findIndex((targetHeader: string) => {
        const targetKey = targetKeyMapping.get(targetHeader);
        return targetKey === sourceKey;
      });
      
      if (targetIndex !== -1) {
        columnMapping.set(sourceIndex, targetIndex);
      }
    }
  });
  
  // Ensure we have at least one column mapping
  if (columnMapping.size === 0) {
    logWarning('No column mappings found between source and target', "table-mapping", undefined);
    return targetData;
  }
  
  // Update target rows where there's a match
  const updatedTargetRows = targetRows.map((targetRow: any[]) => {
    const targetRowKey = buildRowKeyFromIndices(targetRow, targetGrouperIndices);
    const sourceRow = sourceRowMap.get(targetRowKey);
    
    if (sourceRow) {
      // Match found - update target row with source values
      const updatedRow = [...targetRow];
      columnMapping.forEach((targetIndex, sourceIndex) => {
        updatedRow[targetIndex] = sourceRow[sourceIndex];
      });
      return updatedRow;
    }
    
    // No match - keep target row unchanged
    return targetRow;
  });
  
  // Return updated target table
  return [targetHeaders, ...updatedTargetRows];
}

/**
 * Match All Sync with Key-based column mapping
 * Replaces entire target table with source data (matching columns by keys)
 */
export function matchAllSyncWithKeys(
  sourceData: any[][],
  targetData: any[][],
  sourceKeyMapping: Map<string, string>,
  targetKeyMapping: Map<string, string>
): any[][] {
  if (!sourceData || sourceData.length === 0 || !targetData || targetData.length === 0) {
    return targetData;
  }
  
  const sourceHeaders = sourceData[0];
  const targetHeaders = targetData[0];
  const sourceRows = sourceData.slice(1);
  
  // Build column mapping by keys
  const columnMapping = new Map<number, number>();
  sourceHeaders.forEach((sourceHeader: string, sourceIndex: number) => {
    const sourceKey = sourceKeyMapping.get(sourceHeader);
    if (sourceKey) {
      // Find target column with same key
      const targetIndex = targetHeaders.findIndex((targetHeader: string) => {
        const targetKey = targetKeyMapping.get(targetHeader);
        return targetKey === sourceKey;
      });
      
      if (targetIndex !== -1) {
        columnMapping.set(sourceIndex, targetIndex);
      }
    }
  });
  
  // Ensure we have at least one column mapping
  if (columnMapping.size === 0) {
    logWarning('No column mappings found between source and target', "table-mapping", undefined);
    return targetData;
  }
  
  // Create new target rows with source data
  const newTargetRows = sourceRows.map((sourceRow: any[]) => {
    const newRow = new Array(targetHeaders.length).fill('');
    
    // Copy matched columns from source to target
    columnMapping.forEach((targetIndex, sourceIndex) => {
      newRow[targetIndex] = sourceRow[sourceIndex];
    });
    
    return newRow;
  });
  
  // Return new target table with source rows
  return [targetHeaders, ...newTargetRows];
}

/**
 * Match Selected Sync: Update matching rows AND add new rows that don't exist
 * Combines the behavior of "Matching" (update existing) and "All" (add new)
 * Uses key-based column mapping
 */
export function matchSelectedSyncWithKeys(
  sourceData: any[][],
  targetData: any[][],
  grouperColumns: string[],
  sourceKeyMapping: Map<string, string>,
  targetKeyMapping: Map<string, string>
): any[][] {
  if (!sourceData || sourceData.length === 0 || !targetData || targetData.length === 0) {
    return targetData;
  }
  
  const sourceHeaders = sourceData[0];
  const targetHeaders = targetData[0];
  const sourceRows = sourceData.slice(1);
  const targetRows = targetData.slice(1);
  
  // Find grouper column indices
  const sourceGrouperIndices = findGrouperIndicesByName(sourceHeaders, grouperColumns);
  const targetGrouperIndices = findGrouperIndicesByName(targetHeaders, grouperColumns);
  
  if (sourceGrouperIndices.length === 0 || targetGrouperIndices.length === 0) {
    logWarning('No grouper columns found for matching', "table-mapping", undefined);
    return targetData;
  }
  
  // Build source row map by grouper key
  const sourceRowMap = new Map<string, any[]>();
  sourceRows.forEach((sourceRow: any[]) => {
    const rowKey = buildRowKeyFromIndices(sourceRow, sourceGrouperIndices);
    sourceRowMap.set(rowKey, sourceRow);
  });
  
  // Build target row map by grouper key
  const targetRowMap = new Map<string, number>(); // key -> row index
  targetRows.forEach((targetRow: any[], index: number) => {
    const rowKey = buildRowKeyFromIndices(targetRow, targetGrouperIndices);
    targetRowMap.set(rowKey, index);
  });
  
  // Build column mapping by keys
  const columnMapping = new Map<number, number>();
  sourceHeaders.forEach((sourceHeader: string, sourceIndex: number) => {
    const sourceKey = sourceKeyMapping.get(sourceHeader);
    if (sourceKey) {
      // Find target column with same key
      const targetIndex = targetHeaders.findIndex((targetHeader: string) => {
        const targetKey = targetKeyMapping.get(targetHeader);
        return targetKey === sourceKey;
      });
      
      if (targetIndex !== -1) {
        columnMapping.set(sourceIndex, targetIndex);
      }
    }
  });
  
  // Ensure we have at least one column mapping
  if (columnMapping.size === 0) {
    logWarning('No column mappings found between source and target', "table-mapping", undefined);
    return targetData;
  }
  
  // Update existing matching rows
  const updatedTargetRows = [...targetRows];
  
  // Track which source rows we've processed
  const processedSourceRows = new Set<string>();
  
  // Update target rows where there's a match
  updatedTargetRows.forEach((targetRow: any[], targetIndex: number) => {
    const targetRowKey = buildRowKeyFromIndices(targetRow, targetGrouperIndices);
    const sourceRow = sourceRowMap.get(targetRowKey);
    
    if (sourceRow) {
      // Match found - update target row with source values
      columnMapping.forEach((targetColIndex, sourceColIndex) => {
        updatedTargetRows[targetIndex][targetColIndex] = sourceRow[sourceColIndex];
      });
      processedSourceRows.add(targetRowKey);
    }
  });
  
  // Add new rows from source that don't exist in target
  sourceRows.forEach((sourceRow: any[]) => {
    const rowKey = buildRowKeyFromIndices(sourceRow, sourceGrouperIndices);
    
    if (!processedSourceRows.has(rowKey)) {
      // This is a new row - add it to the target
      const newRow = new Array(targetHeaders.length).fill('');
      
      // Copy matched columns from source to target
      columnMapping.forEach((targetIndex, sourceIndex) => {
        newRow[targetIndex] = sourceRow[sourceIndex];
      });
      
      updatedTargetRows.push(newRow);
    }
  });
  
  // Return updated target table
  return [targetHeaders, ...updatedTargetRows];
}

/**
 * Match Only Sync for Target-to-Target (Document → Study)
 * Updates existing study rows where grouper values match document rows
 */
export function matchOnlySyncTargetToTarget(
  sourceData: any[][],
  targetData: any[][],
  grouperColumns: string[],
  keyMapping: Map<string, string>
): any[][] {
  if (!sourceData || sourceData.length === 0 || !targetData || targetData.length === 0) {
    return targetData;
  }
  
  const sourceHeaders = sourceData[0];
  const targetHeaders = targetData[0];
  const sourceRows = sourceData.slice(1);
  const targetRows = targetData.slice(1);
  
  // Build column mapping by keys first (both use same key mapping)
  const columnMapping = new Map<number, number>();
  sourceHeaders.forEach((sourceHeader: string, sourceIndex: number) => {
    const sourceKey = keyMapping.get(sourceHeader);
    if (sourceKey) {
      // Find target column with same key
      const targetIndex = targetHeaders.findIndex((targetHeader: string) => {
        const targetKey = keyMapping.get(targetHeader);
        return targetKey === sourceKey;
      });
      
      if (targetIndex !== -1) {
        columnMapping.set(sourceIndex, targetIndex);
      }
    }
  });
  
  // Find grouper column indices
  let sourceGrouperIndices = findGrouperIndicesByName(sourceHeaders, grouperColumns);
  let targetGrouperIndices = findGrouperIndicesByName(targetHeaders, grouperColumns);
  
  // Fallback: if no grouper columns found, use all mapped columns as key (for references)
  if (sourceGrouperIndices.length === 0 || targetGrouperIndices.length === 0) {
    logWarning('No grouper columns found for matching, using all mapped columns as key', "table-mapping", undefined);
    // For references, match by all columns that have matching keys
    if (columnMapping.size > 0) {
      // Use all source columns that have mappings as the key columns
      sourceGrouperIndices = Array.from(columnMapping.keys());
      // Use corresponding target columns
      targetGrouperIndices = Array.from(columnMapping.values());
    } else if (sourceHeaders.length > 0 && targetHeaders.length > 0 && sourceHeaders.length === targetHeaders.length) {
      // Last resort: if headers match 1:1, use all columns
      sourceGrouperIndices = sourceHeaders.map((_, i) => i);
      targetGrouperIndices = targetHeaders.map((_, i) => i);
    } else {
      logWarning('Cannot match: no valid column mappings available', "table-mapping", undefined);
      return targetData;
    }
  }
  
  // Build source row map by grouper key
  const sourceRowMap = new Map<string, any[]>();
  sourceRows.forEach((sourceRow: any[]) => {
    const rowKey = buildRowKeyFromIndices(sourceRow, sourceGrouperIndices);
    sourceRowMap.set(rowKey, sourceRow);
  });
  
  // Update target rows where there's a match
  const updatedTargetRows = targetRows.map((targetRow: any[]) => {
    const targetRowKey = buildRowKeyFromIndices(targetRow, targetGrouperIndices);
    const sourceRow = sourceRowMap.get(targetRowKey);
    
    if (sourceRow) {
      // Match found - update target row with source values
      const updatedRow = [...targetRow];
      columnMapping.forEach((targetIndex, sourceIndex) => {
        updatedRow[targetIndex] = sourceRow[sourceIndex];
      });
      return updatedRow;
    }
    
    // No match - keep target row unchanged
    return targetRow;
  });
  
  // Return updated target table
  return [targetHeaders, ...updatedTargetRows];
}

/**
 * Match All Sync for Target-to-Target (Document → Study)
 * Replaces entire study table with document data
 */
export function matchAllSyncTargetToTarget(
  sourceData: any[][],
  targetData: any[][],
  keyMapping: Map<string, string>
): any[][] {
  if (!sourceData || sourceData.length === 0 || !targetData || targetData.length === 0) {
    return targetData;
  }
  
  const sourceHeaders = sourceData[0];
  const targetHeaders = targetData[0];
  const sourceRows = sourceData.slice(1);
  
  // Build column mapping by keys (both document and study use same project structure)
  const columnMapping = new Map<number, number>();
  sourceHeaders.forEach((sourceHeader: string, sourceIndex: number) => {
    const sourceKey = keyMapping.get(sourceHeader);
    if (sourceKey) {
      // Find target column with same key
      const targetIndex = targetHeaders.findIndex((targetHeader: string) => {
        const targetKey = keyMapping.get(targetHeader);
        return targetKey === sourceKey;
      });
      
      if (targetIndex !== -1) {
        columnMapping.set(sourceIndex, targetIndex);
      }
    }
  });
  
  // Create new target rows with source data
  const newTargetRows = sourceRows.map((sourceRow: any[]) => {
    const newRow = new Array(targetHeaders.length).fill('');
    
    // Copy matched columns from source to target
    columnMapping.forEach((targetIndex, sourceIndex) => {
      newRow[targetIndex] = sourceRow[sourceIndex];
    });
    
    return newRow;
  });
  
  // Return new target table with source rows
  return [targetHeaders, ...newTargetRows];
}

/**
 * Match Selected Sync for Target-to-Target (Document → Study)
 * Update matching rows AND add new rows that don't exist
 */
export function matchSelectedSyncTargetToTarget(
  sourceData: any[][],
  targetData: any[][],
  grouperColumns: string[],
  keyMapping: Map<string, string>
): any[][] {
  if (!sourceData || sourceData.length === 0 || !targetData || targetData.length === 0) {
    return targetData;
  }
  
  const sourceHeaders = sourceData[0];
  const targetHeaders = targetData[0];
  const sourceRows = sourceData.slice(1);
  const targetRows = targetData.slice(1);
  
  // Build column mapping by keys first (both use same key mapping)
  const columnMapping = new Map<number, number>();
  sourceHeaders.forEach((sourceHeader: string, sourceIndex: number) => {
    const sourceKey = keyMapping.get(sourceHeader);
    if (sourceKey) {
      // Find target column with same key
      const targetIndex = targetHeaders.findIndex((targetHeader: string) => {
        const targetKey = keyMapping.get(targetHeader);
        return targetKey === sourceKey;
      });
      
      if (targetIndex !== -1) {
        columnMapping.set(sourceIndex, targetIndex);
      }
    }
  });
  
  // Find grouper column indices
  let sourceGrouperIndices = findGrouperIndicesByName(sourceHeaders, grouperColumns);
  let targetGrouperIndices = findGrouperIndicesByName(targetHeaders, grouperColumns);
  
  // Fallback: if no grouper columns found, use all mapped columns as key (for references)
  if (sourceGrouperIndices.length === 0 || targetGrouperIndices.length === 0) {
    logWarning('No grouper columns found for matching, using all mapped columns as key', "table-mapping", undefined);
    // For references, match by all columns that have matching keys
    if (columnMapping.size > 0) {
      // Use all source columns that have mappings as the key columns
      sourceGrouperIndices = Array.from(columnMapping.keys());
      // Use corresponding target columns
      targetGrouperIndices = Array.from(columnMapping.values());
    } else if (sourceHeaders.length > 0 && targetHeaders.length > 0 && sourceHeaders.length === targetHeaders.length) {
      // Last resort: if headers match 1:1, use all columns
      sourceGrouperIndices = sourceHeaders.map((_, i) => i);
      targetGrouperIndices = targetHeaders.map((_, i) => i);
    } else {
      logWarning('Cannot match: no valid column mappings available', "table-mapping", undefined);
      return targetData;
    }
  }
  
  // Build source row map by grouper key
  const sourceRowMap = new Map<string, any[]>();
  sourceRows.forEach((sourceRow: any[]) => {
    const rowKey = buildRowKeyFromIndices(sourceRow, sourceGrouperIndices);
    sourceRowMap.set(rowKey, sourceRow);
  });
  
  // Update existing matching rows
  const updatedTargetRows = [...targetRows];
  
  // Track which source rows we've processed
  const processedSourceRows = new Set<string>();
  
  // Update target rows where there's a match
  updatedTargetRows.forEach((targetRow: any[], targetIndex: number) => {
    const targetRowKey = buildRowKeyFromIndices(targetRow, targetGrouperIndices);
    const sourceRow = sourceRowMap.get(targetRowKey);
    
    if (sourceRow) {
      // Match found - update target row with source values
      columnMapping.forEach((targetColIndex, sourceColIndex) => {
        updatedTargetRows[targetIndex][targetColIndex] = sourceRow[sourceColIndex];
      });
      processedSourceRows.add(targetRowKey);
    }
  });
  
  // Add new rows from source that don't exist in target
  sourceRows.forEach((sourceRow: any[]) => {
    const rowKey = buildRowKeyFromIndices(sourceRow, sourceGrouperIndices);
    
    if (!processedSourceRows.has(rowKey)) {
      // This is a new row - add it to the target
      const newRow = new Array(targetHeaders.length).fill('');
      
      // Copy matched columns from source to target
      columnMapping.forEach((targetIndex, sourceIndex) => {
        newRow[targetIndex] = sourceRow[sourceIndex];
      });
      
      updatedTargetRows.push(newRow);
    }
  });
  
  // Return updated target table
  return [targetHeaders, ...updatedTargetRows];
}

